import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { Rule, Violation, ViolationStatus } from '@/types/rule';
import { getAccessToken } from '@/utils/jwt';

export interface OrphanLink {
  id: string;
  entity_type: string;
  external_id: string;
  source_system: string;
  extra_data: Record<string, unknown>;
}

export interface AutoLinkedRecord {
  id: string;
  entity_type: string;
  external_id: string;
  source_system: string;
  entity_id: string;
  extra_data: Record<string, unknown>;
}

export const rulesApi = createApi({
  reducerPath: 'rulesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_AGENT_MCP_URL || '/agent',
    prepareHeaders: (headers) => {
      const token = getAccessToken();
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Rule', 'Violation', 'Orphan', 'AutoLinked'],
  endpoints: (builder) => ({
    listRules: builder.query<Rule[], { severity?: string; evaluator_type?: string }>({
      query: (params) => ({ url: '/rules', params }),
      transformResponse: (response: { rules: Rule[]; count: number }) => response.rules,
      providesTags: ['Rule'],
    }),
    getRule: builder.query<Rule, string>({
      query: (id) => `/rules/${id}`,
      transformResponse: (response: { rule: Rule }) => response.rule,
      providesTags: (_r, _e, id) => [{ type: 'Rule', id }],
    }),
    createRule: builder.mutation<Rule, Partial<Rule>>({
      query: (body) => ({ url: '/rules', method: 'POST', body }),
      transformResponse: (response: { rule: Rule }) => response.rule,
      invalidatesTags: ['Rule'],
    }),
    updateRule: builder.mutation<Rule, { id: string; body: Partial<Rule> }>({
      query: ({ id, body }) => ({ url: `/rules/${id}`, method: 'PUT', body }),
      transformResponse: (response: { rule: Rule }) => response.rule,
      invalidatesTags: (_r, _e, { id }) => [{ type: 'Rule', id }],
    }),
    deleteRule: builder.mutation<undefined, string>({
      query: (id) => ({ url: `/rules/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Rule'],
    }),
    evaluateRule: builder.mutation<{ violations: Violation[] }, string>({
      query: (id) => ({ url: `/rules/${id}/evaluate`, method: 'POST' }),
      invalidatesTags: ['Violation'],
    }),
    listViolations: builder.query<
      Violation[],
      { severity?: string; status?: string; category?: string }
    >({
      query: (params) => ({ url: '/rules/violations', params }),
      transformResponse: (response: { violations: Violation[]; count: number }) =>
        response.violations,
      providesTags: ['Violation'],
    }),
    updateViolationStatus: builder.mutation<Violation, { id: string; status: ViolationStatus }>({
      query: ({ id, status }) => ({
        url: `/rules/violations/${id}/status`,
        method: 'PUT',
        body: { status },
      }),
      transformResponse: (response: { violation: Violation }) => response.violation,
      invalidatesTags: ['Violation'],
    }),
    mergeEntities: builder.mutation<
      { merged: boolean; links_moved: number },
      { entityType: string; targetId: string; sourceId: string }
    >({
      query: ({ entityType, targetId, sourceId }) => ({
        url: `/${entityType}/${targetId}/merge`,
        method: 'POST',
        body: { source_entity_id: sourceId },
      }),
      invalidatesTags: ['Violation'],
    }),
    dismissDuplicate: builder.mutation<{ dismissed: boolean }, { violationId: string }>({
      query: ({ violationId }) => ({
        url: `/violations/${violationId}/dismiss`,
        method: 'POST',
      }),
      invalidatesTags: ['Violation'],
    }),
    listOrphans: builder.query<OrphanLink[], { source_system?: string }>({
      query: (params) => ({ url: '/linking/orphans', params }),
      transformResponse: (r: { orphans: OrphanLink[] }) => r.orphans,
      providesTags: ['Orphan'],
    }),
    linkOrphan: builder.mutation<undefined, { linkId: string; projectId: string }>({
      query: ({ linkId, projectId }) => ({
        url: `/linking/orphans/${linkId}/link`,
        method: 'POST',
        body: { project_id: projectId },
      }),
      invalidatesTags: ['Orphan', 'Violation'],
    }),
    dismissOrphan: builder.mutation<undefined, string>({
      query: (linkId) => ({
        url: `/linking/orphans/${linkId}/dismiss`,
        method: 'POST',
      }),
      invalidatesTags: ['Orphan'],
    }),
    listAutoLinked: builder.query<AutoLinkedRecord[], undefined>({
      query: () => '/linking/auto-linked',
      transformResponse: (r: { links: AutoLinkedRecord[] }) => r.links,
      providesTags: ['AutoLinked'],
    }),
    unlinkAutoLinked: builder.mutation<undefined, string>({
      query: (linkId) => ({
        url: `/linking/auto-linked/${linkId}/unlink`,
        method: 'POST',
      }),
      invalidatesTags: ['AutoLinked', 'Orphan'],
    }),
  }),
});

export const {
  useListRulesQuery,
  useGetRuleQuery,
  useCreateRuleMutation,
  useUpdateRuleMutation,
  useDeleteRuleMutation,
  useEvaluateRuleMutation,
  useListViolationsQuery,
  useUpdateViolationStatusMutation,
  useMergeEntitiesMutation,
  useDismissDuplicateMutation,
  useListOrphansQuery,
  useLinkOrphanMutation,
  useDismissOrphanMutation,
  useListAutoLinkedQuery,
  useUnlinkAutoLinkedMutation,
} = rulesApi;
